import {
  type KeyboardEvent as ReactKeyboardEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type NavigationContextType = {
  isLastInput: boolean;
};

const NavigationContext = createContext<NavigationContextType>({
  isLastInput: false,
});

export const useNavigationContext = () => useContext(NavigationContext);

type Props = {
  id?: string;
  nextKeys?: KeyboardEvent["code"][];
  backKeys?: KeyboardEvent["code"][];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  children: ({
    onKeyDown,
  }: {
    onKeyDown: (
      e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>,
    ) => void;
  }) => React.ReactNode;
  loop?: boolean;
};

export const BankBookingsNavigation = ({
  id = "bank-bookings-navigation",
  nextKeys = ["Enter", "NumpadEnter", "NumpadAdd"],
  backKeys = ["NumpadSubtract"],
  loop = false,
  className,
  children,
}: Props) => {
  const [isLastInput, setIsLastInput] = useState(false);

  const checkIsLastInput = useCallback(() => {
    const div = document.getElementById(id);
    if (!div) return;

    const inputs = div.querySelectorAll(
      "[data-navigation-index] input, [data-navigation-index] select, [data-navigation-index] textarea, [data-navigation-index] button",
    );

    if (!inputs.length) return;

    let activeElement: HTMLElement | null =
      document.activeElement as HTMLElement;

    if (!div.contains(activeElement)) {
      setIsLastInput(false);
      return;
    }

    const activeElementRole = activeElement?.getAttribute("role");

    if (activeElementRole === "combobox") {
      const comboBoxId = activeElement?.getAttribute("data-combobox-id");
      const comboInputs = div.querySelectorAll(
        `[data-navigation-index] [data-combobox-id="${comboBoxId}"]`,
      );
      activeElement = comboInputs?.[0] as HTMLElement;
    }

    const activeElementIndex = Array.from(inputs).indexOf(
      activeElement as HTMLElement,
    );

    const nextElement = inputs?.[activeElementIndex + 1] as HTMLElement;

    const isLast =
      !nextElement ||
      (nextElement instanceof HTMLButtonElement &&
        nextElement.type === "submit");

    setIsLastInput(isLast);
  }, [id]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      const div = document.getElementById(id);
      if (!div) return;

      const activeElement = document.activeElement;

      if (!div.contains(activeElement)) {
        setIsLastInput(false);
      }
    }, 0);
  }, [id]);

  const focusElement = (element: HTMLElement) => {
    if (!element) return;

    if (element.role === "combobox") {
      element.click();
    } else {
      element.focus();
    }
  };

  const handleKeyDown = (
    e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>,
  ) => {
    // blur on escape
    if (e.key === "Escape") {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }

    if (nextKeys.includes(e.code)) {
      e.preventDefault();
      const div = document.getElementById(id);

      const inputs = div?.querySelectorAll(
        "[data-navigation-index] input, [data-navigation-index] select, [data-navigation-index] textarea, [data-navigation-index] button",
      );

      let activeElement: HTMLElement | null =
        document.activeElement as HTMLElement;

      const activeElementRole = activeElement?.getAttribute("role");

      let nextElement: HTMLElement | null = null;

      if (activeElementRole === "combobox") {
        const comboBoxId = activeElement?.getAttribute("data-combobox-id");

        const inputs = div?.querySelectorAll(
          `[data-navigation-index] [data-combobox-id="${comboBoxId}"]`,
        );

        activeElement = inputs?.[0] as HTMLElement;
      }

      const activeElementIndex = inputs
        ? Array.from(inputs).indexOf(activeElement as HTMLElement)
        : -1;

      nextElement = inputs?.[activeElementIndex + 1] as HTMLElement;

      if (
        nextElement instanceof HTMLButtonElement &&
        nextElement.type === "submit"
      ) {
        nextElement.click();
        return;
      }

      if (!nextElement && loop) {
        const firstElement = inputs?.[0];
        focusElement(firstElement as HTMLElement);
        setTimeout(checkIsLastInput, 10);
        return;
      }

      focusElement(nextElement as HTMLElement);
      setTimeout(checkIsLastInput, 10);
    }
    if (backKeys.includes(e.code)) {
      const target = e.currentTarget as HTMLInputElement;
      if (
        target.type === "number" ||
        target.inputMode === "numeric" ||
        target.inputMode === "decimal"
      ) {
        return;
      }
      e.preventDefault();
      const div = document.getElementById(id);

      const inputs = div?.querySelectorAll(
        "[data-navigation-index] input, [data-navigation-index] select, [data-navigation-index] textarea, [data-navigation-index] button",
      );

      let activeElement: HTMLElement | null =
        document.activeElement as HTMLElement;

      const activeElementRole = activeElement?.getAttribute("role");

      let prevElement: HTMLElement | null = null;

      if (activeElementRole === "combobox") {
        const comboBoxId = activeElement?.getAttribute("data-combobox-id");

        const inputs = div?.querySelectorAll(
          `[data-navigation-index] [data-combobox-id="${comboBoxId}"]`,
        );

        activeElement = inputs?.[0] as HTMLElement;
      }

      const activeElementIndex = inputs
        ? Array.from(inputs).indexOf(activeElement as HTMLElement)
        : -1;

      prevElement = inputs?.[activeElementIndex - 1] as HTMLElement;

      if (!prevElement && loop) {
        const lastElement = inputs?.[inputs.length - 1];

        focusElement(lastElement as HTMLElement);
        return;
      }

      focusElement(prevElement as HTMLElement);
      setTimeout(checkIsLastInput, 10);
    }
  };

  return (
    <NavigationContext.Provider value={{ isLastInput }}>
      <div
        className={className}
        id={id}
        onFocusCapture={checkIsLastInput}
        onBlurCapture={handleBlur}
      >
        {children({ onKeyDown: handleKeyDown })}
      </div>
    </NavigationContext.Provider>
  );
};

export const NavigationElement = ({
  children,
}: { children: React.ReactNode }) => {
  const divRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={divRef} data-navigation-index className="relative">
      {children}
    </div>
  );
};
